import React from "react"
import { Button, CardTitle, Table } from "reactstrap"
import { useTranslation } from "react-i18next"
import {
  canShowManualPosPushButton,
  formatPosPushErrorMessage,
  getPosIntegrationDisplayName,
  getTenantPosConnectionLabel,
  isFoodicsProvider,
  isWuloojPosProvider,
  resolveOrderPosPushState,
} from "utils/orderPosUtils"
import {
  FOODICS_DELIVERY_STATUS_LABELS,
  getPosStatusColor,
  getPosStatusLabel,
} from "utils/orderStatusUtils"

const OrderPosDetailsPanel = ({
  order = {},
  posSource = {},
  posIntegrationCode = "",
  tenantConnection = {},
  branchLinked = false,
  onManualPush,
  pushLoading = false,
  canEdit = false,
  copyToClipboard,
}) => {
  const { t } = useTranslation()
  const isFoodics = isFoodicsProvider(posSource)
  const isWulooj = isWuloojPosProvider(posSource)
  const pushState = resolveOrderPosPushState(order)
  const showPushButton = canShowManualPosPushButton({
    order,
    posIntegrationCode,
    tenantConnected: tenantConnection.connected,
    branchLinked,
    canEdit,
  })

  const posStatusLabel = getPosStatusLabel(order, t)
  const posStatusColor = getPosStatusColor(order)

  const branchIdValue = isWulooj
    ? order?.posSettings?.wuloojBranchId
    : isFoodics
      ? order?.posSettings?.foodicsBranchId
      : order?.posSettings?.organizationId

  const branchLabel = isWulooj
    ? t("Wulooj Branch Id")
    : isFoodics
      ? t("Foodics Branch Id")
      : t("Organization Id")

  return (
    <>
      <CardTitle className="mt-4 d-flex align-items-center">
        {t("POS Details")}
      </CardTitle>
      <Table className="mb-0">
        <tbody>
          <tr>
            <th scope="row">{t("POS Integration")}</th>
            <td>{getPosIntegrationDisplayName(posIntegrationCode, t)}</td>
          </tr>
          <tr>
            <th scope="row">{t("POS Integration Status")}</th>
            <td
              className={
                tenantConnection.connected ? "text-success" : "text-danger"
              }
            >
              {getTenantPosConnectionLabel(tenantConnection, t)}
            </td>
          </tr>
          <tr>
            <th scope="row">{t("Branch linked to POS")}</th>
            <td className={branchLinked ? "text-success" : "text-danger"}>
              {branchLinked ? t("Yes") : t("No")}
            </td>
          </tr>
          <tr>
            <th scope="row">{branchLabel}</th>
            <td>
              {branchIdValue || "-"}
              {branchIdValue && copyToClipboard && (
                <i
                  className="mdi mdi-content-copy cursor-pointer text-primary fs-4 ms-2"
                  role="button"
                  tabIndex={0}
                  onClick={() => copyToClipboard(branchIdValue)}
                  onKeyDown={() => {}}
                />
              )}
            </td>
          </tr>
          {!isFoodics && !isWulooj && (
            <tr>
              <th scope="row">{t("Terminal Id")}</th>
              <td>
                {order?.posSettings?.terminalGroupId || "-"}
                {order?.posSettings?.terminalGroupId && copyToClipboard && (
                  <i
                    className="mdi mdi-content-copy cursor-pointer text-primary fs-4 ms-2"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      copyToClipboard(order.posSettings.terminalGroupId)
                    }
                    onKeyDown={() => {}}
                  />
                )}
              </td>
            </tr>
          )}
          <tr>
            <th scope="row">{t("POS Reference Order Id")}</th>
            <td>
              {order?.posReferenceId || "-"}
              {order?.posReferenceId && copyToClipboard && (
                <i
                  className="mdi mdi-content-copy cursor-pointer text-primary fs-4 ms-2"
                  role="button"
                  tabIndex={0}
                  onClick={() => copyToClipboard(order.posReferenceId)}
                  onKeyDown={() => {}}
                />
              )}
            </td>
          </tr>
          {isWulooj && order?.posSettings?.wuloojOrderNumber && (
            <tr>
              <th scope="row">{t("Wulooj Order Number")}</th>
              <td>{order.posSettings.wuloojOrderNumber}</td>
            </tr>
          )}
          {isFoodics && (
            <tr>
              <th scope="row">{t("Foodics Delivery Status")}</th>
              <td>
                {order?.posSettings?.deliveryStatus != null &&
                FOODICS_DELIVERY_STATUS_LABELS[
                  Number(order.posSettings.deliveryStatus)
                ]
                  ? t(
                      FOODICS_DELIVERY_STATUS_LABELS[
                        Number(order.posSettings.deliveryStatus)
                      ]
                    )
                  : "-"}
              </td>
            </tr>
          )}
          <tr>
            <th scope="row">{t("POS Status")}</th>
            <td>
              <div className="d-flex flex-wrap align-items-center gap-3">
                <p className="mb-0" style={{ color: posStatusColor }}>
                  {posStatusLabel}
                </p>
                {showPushButton && (
                  <Button
                    color="primary"
                    size="sm"
                    title={t("Sync Order With POS")}
                    onClick={onManualPush}
                    disabled={pushLoading}
                  >
                    {t("Send on POS")}
                  </Button>
                )}
              </div>
            </td>
          </tr>
          {order?.posSettings?.lastPushOutcome && (
            <tr>
              <th scope="row">{t("Last POS push")}</th>
              <td>
                {order.posSettings.lastPushOutcome}
                {order.posSettings.lastPushAt
                  ? ` · ${new Date(order.posSettings.lastPushAt).toLocaleString()}`
                  : ""}
              </td>
            </tr>
          )}
          {(order?.posSettings?.lastPushReason ||
            pushState.reason) && (
            <tr>
              <th scope="row">{t("Last push detail")}</th>
              <td className="text-muted">
                {formatPosPushErrorMessage(
                  order?.posSettings?.lastPushReason || pushState.reason
                ) || "-"}
              </td>
            </tr>
          )}
          {order?.posSettings?.errorInfo && (
            <tr>
              <th scope="row">{t("Error Information")}</th>
              <td>
                <p className="text-danger mb-0">
                  {formatPosPushErrorMessage(order.posSettings.errorInfo)}
                </p>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  )
}

export default OrderPosDetailsPanel
